import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import axios from "axios";
import {
  Calculator,
  Download,
  FileText,
  Package,
  AlertTriangle,
  Search,
  Plus,
  Trash2,
  Loader2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * RM Required — Procurement page.
 *
 * Two modes:
 *  - Schedule: pulls production_schedules for branch within date range and
 *              aggregates RM requirement vs current branch stock.
 *  - Manual:   user supplies [{buyer_sku_id, quantity}] rows and the same
 *              aggregation is computed.
 *
 * Backend: POST /api/rm-required (see backend/routes/branch_ops_routes.py)
 */

const todayISO = () => new Date().toISOString().split("T")[0];
const addDaysISO = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().split("T")[0];
};

// --- Manual line row with debounced buyer SKU autocomplete ---
const ManualLineRow = ({ idx, line, onChange, onRemove }) => {
  const [query, setQuery] = useState(line.buyer_sku_id || "");
  const [results, setResults] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  // Feb 2026 — Phase 2 migration (defect A). reqId stale-guard.
  const reqIdRef = useRef(0);

  useEffect(() => {
    setQuery(line.buyer_sku_id || "");
  }, [line.buyer_sku_id]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = (query || "").trim();
    if (!q || q.length < 2) {
      // Feb 2026 — invalidate any in-flight request so its late response
      // can't repopulate stale options under a sub-minimum query.
      ++reqIdRef.current;
      setResults([]);
      // Clear the searching flag so the visible min-char hint isn't
      // hidden after backspacing.
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const myReqId = ++reqIdRef.current;
      try {
        setSearching(true);
        const res = await axios.get(
          `${API}/sku-management/buyer-skus?search=${encodeURIComponent(q)}&page=1&page_size=15`
        );
        if (myReqId !== reqIdRef.current) return;
        setResults(res.data?.items || []);
      } catch (_e) {
        if (myReqId !== reqIdRef.current) return;
        setResults([]);
      } finally {
        if (myReqId === reqIdRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const pick = (sku) => {
    onChange(idx, { buyer_sku_id: sku.buyer_sku_id, name: sku.name || "" });
    setQuery(sku.buyer_sku_id);
    setShowDrop(false);
  };

  return (
    <tr className="border-t" data-testid={`manual-row-${idx}`}>
      <td className="px-2 py-2 text-center text-xs font-mono text-muted-foreground">
        {idx + 1}
      </td>
      <td className="px-2 py-2 relative" ref={containerRef}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search Buyer SKU (id or name)..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDrop(true);
              onChange(idx, { buyer_sku_id: e.target.value, name: line.name });
            }}
            onFocus={() => setShowDrop(true)}
            className="pl-8 font-mono text-xs h-9"
            data-testid={`manual-row-sku-input-${idx}`}
          />
        </div>
        {showDrop && (results.length > 0 || searching || (query.trim().length > 0 && query.trim().length < 2)) && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto">
            {/* Feb 2026 — defect C: visible min-char gate hint. */}
            {!searching && query.trim().length > 0 && query.trim().length < 2 && (
              <div className="px-3 py-2 text-xs text-muted-foreground italic" data-testid={`manual-row-sku-hint-${idx}`}>
                Type at least 2 characters…
              </div>
            )}
            {searching && (
              <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Searching…
              </div>
            )}
            {results.map((s) => (
              <button
                type="button"
                key={s.buyer_sku_id}
                onClick={() => pick(s)}
                className="w-full text-left px-3 py-2 hover:bg-zinc-100 border-b last:border-b-0"
                data-testid={`manual-row-pick-${idx}-${s.buyer_sku_id}`}
              >
                <div className="font-mono text-xs font-bold">{s.buyer_sku_id}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {s.name || s.bidso_sku_id}
                </div>
              </button>
            ))}
          </div>
        )}
      </td>
      <td className="px-2 py-2 text-xs text-muted-foreground max-w-[260px] truncate">
        {line.name || "—"}
      </td>
      <td className="px-2 py-2 w-32">
        <Input
          type="number"
          min={0}
          step="any"
          value={line.quantity ?? ""}
          onChange={(e) =>
            onChange(idx, { quantity: e.target.value })
          }
          className="font-mono text-xs h-9 text-right"
          data-testid={`manual-row-qty-input-${idx}`}
        />
      </td>
      <td className="px-2 py-2 w-12 text-center">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onRemove(idx)}
          data-testid={`manual-row-remove-${idx}`}
          title="Remove line"
        >
          <Trash2 className="w-4 h-4 text-rose-600" />
        </Button>
      </td>
    </tr>
  );
};

const RMRequired = () => {
  const user = useAuthStore((s) => s.user);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [mode, setMode] = useState("schedule"); // "schedule" | "manual"
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(addDaysISO(todayISO(), 7));
  const [manualLines, setManualLines] = useState([
    { buyer_sku_id: "", name: "", quantity: "" },
  ]);
  const [searchFilter, setSearchFilter] = useState("");
  const [showShortageOnly, setShowShortageOnly] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // rm_id -> bool

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/branches`);
        const active = (res.data || []).filter((b) => b.is_active !== false);
        setBranches(active);
        if (active.length && !branch) setBranch(active[0].name);
      } catch (_e) {
        // silent
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addManualLine = () =>
    setManualLines((rows) => [...rows, { buyer_sku_id: "", name: "", quantity: "" }]);

  const updateManualLine = (idx, patch) =>
    setManualLines((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );

  const removeManualLine = (idx) =>
    setManualLines((rows) =>
      rows.length === 1
        ? [{ buyer_sku_id: "", name: "", quantity: "" }]
        : rows.filter((_, i) => i !== idx)
    );

  const calculate = async () => {
    if (!branch) {
      toast.error("Please pick a branch first");
      return;
    }
    const payload = { branch, mode };
    if (mode === "schedule") {
      if (!startDate || !endDate) {
        toast.error("Please pick a date range");
        return;
      }
      payload.start_date = startDate;
      payload.end_date = endDate;
    } else {
      const cleanLines = manualLines
        .map((l) => ({
          buyer_sku_id: (l.buyer_sku_id || "").trim(),
          quantity: Number(l.quantity) || 0,
        }))
        .filter((l) => l.buyer_sku_id && l.quantity > 0);
      if (cleanLines.length === 0) {
        toast.error("Add at least one valid manual row (SKU + quantity > 0)");
        return;
      }
      payload.lines = cleanLines;
    }

    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/rm-required`, payload);
      setResult(res.data);
      if ((res.data?.warnings || []).length > 0) {
        toast.warning(
          `${res.data.warnings.length} warning${res.data.warnings.length === 1 ? "" : "s"} — see panel below.`
        );
      } else {
        toast.success(
          `Calculated ${res.data?.totals?.rms_required || 0} RMs (${
            res.data?.totals?.rms_in_shortage || 0
          } in shortage)`
        );
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to calculate RM required";
      toast.error(typeof msg === "string" ? msg : "Failed to calculate");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const items = result?.items || [];
    const q = searchFilter.trim().toLowerCase();
    return items.filter((it) => {
      if (showShortageOnly && !it.is_shortage) return false;
      if (!q) return true;
      return (
        it.rm_id?.toLowerCase().includes(q) ||
        it.description?.toLowerCase().includes(q) ||
        it.category?.toLowerCase().includes(q)
      );
    });
  }, [result, searchFilter, showShortageOnly]);

  const toggleExpand = (rmId) =>
    setExpanded((m) => ({ ...m, [rmId]: !m[rmId] }));

  // Stable sort: category alphabetical → shortages first → rm_id alphabetical.
  // Used by both PDF and Excel exports so stores team gets a consistent,
  // category-by-category pick list.
  const sortForExport = (items) =>
    [...items].sort((a, b) => {
      const ca = (a.category || "").toLowerCase();
      const cb = (b.category || "").toLowerCase();
      if (ca !== cb) return ca < cb ? -1 : 1;
      if (a.is_shortage !== b.is_shortage) return a.is_shortage ? -1 : 1;
      return (a.rm_id || "").localeCompare(b.rm_id || "");
    });

  const exportXlsx = () => {
    if (!result || (result.items || []).length === 0) {
      toast.error("Nothing to export — run a calculation first");
      return;
    }
    const inputSkus = result.source?.input_skus || [];
    const summary = [
      ["Branch", result.branch || ""],
      ["Mode", result.mode || ""],
      [
        "Period",
        result.source?.period || `${manualLines.length} manual line(s)`,
      ],
      ["Total RMs Required", result.totals?.rms_required ?? 0],
      ["RMs in Shortage", result.totals?.rms_in_shortage ?? 0],
      ["Input Buyer SKUs", inputSkus.length],
      [
        "Total Input Quantity",
        inputSkus.reduce((s, r) => s + Number(r.quantity || 0), 0),
      ],
      [
        "Generated At",
        new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
      ],
    ];
    const sortedItems = sortForExport(result.items || []);
    const rows = sortedItems.map((it, i) => {
      // Uniform UOM Standard (Jul 2026): every export cell carries the
      // authoritative `display_uom`; CONVERSION_MISSING rows leave
      // Stock/Shortage blank (not 0) and get their raw procurement-UOM
      // stock in a dedicated `Stock (Stock UOM)` column. Dual-UOM rows
      // ALSO fill that stock-UOM column with the KG audit copy so the
      // spreadsheet is self-documenting.
      const isMissing = it.uom_status === "CONVERSION_MISSING";
      const displayUom = it.display_uom || it.unit || "?";
      return {
        "#": i + 1,
        Category: it.category || "",
        "RM ID": it.rm_id,
        Description: it.description || "",
        Pantone: it.pantone || "",
        "Preferred MB": it.preferred_mb || "",
        UOM: displayUom,
        [`Required Qty (${displayUom})`]: it.required_qty,
        [`Available Stock (${displayUom})`]: isMissing ? "" : it.available_stock,
        [`Shortage (${displayUom})`]: isMissing ? "" : it.shortage,
        "Stock (Stock UOM)": it.available_stock_stock_uom,
        "Stock UOM": it.stock_uom || "",
        "Is Shortage?": isMissing ? "" : (it.is_shortage ? "YES" : "NO"),
        "UOM Status": it.uom_status || "OK",
        "Contributing SKUs": (it.contributing_skus || [])
          .map(
            (s) =>
              `${s.buyer_sku_id} × ${s.qty} (${s.rm_per_unit} ${displayUom}/unit)`
          )
          .join(" | "),
      };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([["RM REQUIRED REPORT"], [], ...summary]),
      "Summary"
    );
    // Input SKUs sheet — what drove the calculation
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        inputSkus.length
          ? inputSkus.map((s, i) => ({
              "#": i + 1,
              "Buyer SKU": s.buyer_sku_id,
              Name: s.name || "",
              Quantity: s.quantity,
            }))
          : [{ "#": "", "Buyer SKU": "(none)", Name: "", Quantity: "" }]
      ),
      "Input SKUs"
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "RM Required");
    if ((result.warnings || []).length > 0) {
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          (result.warnings || []).map((w, i) => ({ "#": i + 1, Warning: w }))
        ),
        "Warnings"
      );
    }
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fname = `rm_required_${(result.branch || "branch").replace(/\s+/g, "_")}_${
      result.mode
    }_${todayISO()}.xlsx`;
    saveAs(new Blob([out], { type: "application/octet-stream" }), fname);
    toast.success("Export downloaded");
  };

  // --- Printable PDF for the stores team (matches on-screen filters) ---
  const exportPdf = () => {
    if (!result || (result.items || []).length === 0) {
      toast.error("Nothing to export — run a calculation first");
      return;
    }
    if (filtered.length === 0) {
      toast.error("Current filters hide all rows — adjust and try again");
      return;
    }

    // Sort: category alphabetical → shortages first → rm_id (stores can
    // close one category before moving to the next).
    const rows = sortForExport(filtered);

    const now = new Date();
    const istNow = now.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const pad2 = (n) => String(n).padStart(2, "0");
    const docNo = `RMR-${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(
      now.getDate()
    )}-${pad2(now.getHours())}${pad2(now.getMinutes())}`;

    const filterLabel = showShortageOnly ? "Shortages Only" : "All RMs";
    const periodLabel =
      result.mode === "schedule"
        ? result.source?.period || "—"
        : `Manual — ${result.source?.lines_processed ?? 0} line(s)`;

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;

    // --- Header block ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("FACTORY OPS", margin, 16);
    doc.setFontSize(11);
    doc.text("RM REQUIRED — STORES PICK LIST", margin, 22);
    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, 25, pageWidth - margin, 25);

    // --- Context box ---
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const ctxLeftX = margin;
    const ctxRightX = pageWidth / 2 + 4;
    let y = 31;
    const ctxLine = (label, value, x) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, x, y);
      doc.setFont("helvetica", "normal");
      doc.text(String(value ?? "—"), x + 28, y);
    };
    ctxLine("Branch", result.branch || "—", ctxLeftX);
    ctxLine("Doc #", docNo, ctxRightX);
    y += 5;
    ctxLine(
      "Mode",
      result.mode === "schedule" ? "From Schedule" : "Manual SKUs",
      ctxLeftX
    );
    ctxLine("Generated At", `${istNow} IST`, ctxRightX);
    y += 5;
    ctxLine("Period", periodLabel, ctxLeftX);
    ctxLine("Generated By", user?.email || "—", ctxRightX);
    y += 5;
    ctxLine("Filter", filterLabel, ctxLeftX);
    ctxLine(
      "Totals",
      `${rows.length} row(s) • ${rows.filter((r) => r.is_shortage).length} shortage(s)`,
      ctxRightX
    );
    y += 4;

    // --- Input Buyer SKUs block (between context and data table) ---
    const inputSkus = result.source?.input_skus || [];
    let nextStartY = y + 2;
    if (inputSkus.length > 0) {
      const totalQty = inputSkus.reduce(
        (s, r) => s + Number(r.quantity || 0),
        0
      );
      autoTable(doc, {
        startY: y + 4,
        head: [
          [
            {
              content: `INPUT BUYER SKUs (${inputSkus.length}) — Requirement generated for the SKUs below`,
              colSpan: 4,
              styles: {
                fillColor: [50, 50, 50],
                textColor: 255,
                halign: "left",
                fontStyle: "bold",
                fontSize: 8.5,
              },
            },
          ],
          [
            { content: "#", styles: { halign: "center" } },
            { content: "Buyer SKU", styles: { halign: "left" } },
            { content: "Name", styles: { halign: "left" } },
            { content: "Quantity", styles: { halign: "right" } },
          ],
        ],
        body: [
          ...inputSkus.map((s, i) => [
            i + 1,
            s.buyer_sku_id || "",
            s.name || "",
            Number(s.quantity || 0).toLocaleString(),
          ]),
          [
            {
              content: `Total Input Quantity`,
              colSpan: 3,
              styles: { halign: "right", fontStyle: "bold" },
            },
            {
              content: Number(totalQty).toLocaleString(),
              styles: { halign: "right", fontStyle: "bold" },
            },
          ],
        ],
        styles: {
          font: "helvetica",
          fontSize: 8,
          cellPadding: 1.6,
          lineColor: 80,
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [220, 220, 220],
          textColor: 20,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "center", cellWidth: 10 },
          1: { halign: "left", cellWidth: 36, fontStyle: "bold" },
          2: { halign: "left" },
          3: { halign: "right", cellWidth: 26 },
        },
        margin: { left: margin, right: margin },
      });
      nextStartY = (doc.lastAutoTable?.finalY || y + 4) + 6;
    }

    // --- Data table ---
    const missingRows = (rows || []).filter((r) => r.uom_status === "CONVERSION_MISSING");
    autoTable(doc, {
      startY: nextStartY,
      head: [
        [
          "#",
          "RM ID",
          "Description",
          "Cat",
          "Pantone",
          "Pref. MB",
          "UoM",
          "Reqd",
          "Stock",
          "Short",
        ],
      ],
      body: rows.map((it, i) => {
        // Uniform UOM Standard (Jul 2026) — display_uom is the authoritative
        // UOM label; the legacy `unit` field survives only as a fallback for
        // very old responses. CONVERSION_MISSING rows must not print 0 —
        // they render raw procurement-UOM stock with the stock_uom label
        // and "—" for shortage; the `!` prefix + footnote below identifies
        // them for the operator.
        const uom = it.display_uom || it.unit || "?";
        const isMissing = it.uom_status === "CONVERSION_MISSING";
        const rmCell = (isMissing ? "! " : "") + (it.rm_id || "");
        const reqCell = Number(it.required_qty || 0).toLocaleString();
        let stockCell;
        let shortCell;
        if (isMissing) {
          const rawStock = it.available_stock_stock_uom;
          stockCell = rawStock != null
            ? `${Number(rawStock).toLocaleString()} ${it.stock_uom || ""}`.trim()
            : "—";
          shortCell = "—";
        } else if (it.is_dual_uom && it.stock_uom) {
          // Dual-UOM: pieces primary, "≈ x KG" secondary in the same cell.
          const rawStock = it.available_stock_stock_uom;
          const secondary = rawStock != null
            ? `\n≈ ${Number(rawStock).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${it.stock_uom}`
            : "";
          stockCell = `${Number(it.available_stock || 0).toLocaleString()}${secondary}`;
          shortCell = it.is_shortage
            ? Number(it.shortage || 0).toLocaleString()
            : "0";
        } else {
          stockCell = Number(it.available_stock || 0).toLocaleString();
          shortCell = it.is_shortage
            ? Number(it.shortage || 0).toLocaleString()
            : "0";
        }
        return [
          i + 1,
          rmCell,
          it.description || "",
          it.category || "",
          it.pantone || "—",
          it.preferred_mb || "—",
          uom,
          reqCell,
          stockCell,
          shortCell,
        ];
      }),
      styles: {
        font: "helvetica",
        fontSize: 8,
        cellPadding: 1.6,
        lineColor: 80,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [30, 30, 30],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 8 },
        1: { halign: "left", cellWidth: 22, fontStyle: "bold" },
        2: { halign: "left", cellWidth: 50 },
        3: { halign: "center", cellWidth: 10 },
        4: { halign: "left", cellWidth: 22 },
        5: { halign: "left", cellWidth: 22 },
        6: { halign: "center", cellWidth: 12 },
        7: { halign: "right", cellWidth: 16 },
        8: { halign: "right", cellWidth: 20 },
        9: { halign: "right", cellWidth: 18, fontStyle: "bold" },
      },
      // Bold + light grey background on shortage rows (B&W-safe)
      didParseCell: (data) => {
        if (data.section === "body") {
          const r = rows[data.row.index];
          if (r && r.is_shortage) {
            data.cell.styles.fillColor = [235, 235, 235];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      margin: { left: margin, right: margin },
      didDrawPage: () => {
        // Footer doc-id only; page numbers are stamped in a final pass after
        // the sign-off block is drawn (so total page count is accurate).
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(docNo, margin, pageHeight - 6);
      },
    });

    // --- CONVERSION_MISSING footnote (Uniform UOM Standard, Jul 2026) ---
    // Printed BEFORE the sign-off block so an approver reading the pick
    // list from top to bottom sees why some rows carry "! / —" cells.
    if (missingRows.length > 0) {
      const noteY = (doc.lastAutoTable?.finalY || y + 10) + 4;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      const rmList = missingRows.map((r) => r.rm_id).join(", ");
      const noteText = `! = UOM conversion missing — comparison skipped: ${rmList}`;
      // Wrap long RM lists across multiple lines within the printable width.
      const wrapped = doc.splitTextToSize(noteText, pageWidth - margin * 2);
      doc.text(wrapped, margin, noteY);
    }

    // --- Sign-off block (only if it fits on current page, else new page) ---
    const afterTableY = doc.lastAutoTable?.finalY || y + 10;
    const signOffHeight = 38;
    let signOffY = afterTableY + 6;
    if (signOffY + signOffHeight > pageHeight - 14) {
      doc.addPage();
      signOffY = 18;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("HANDOVER / SIGN-OFF", margin, signOffY);
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);
    doc.rect(margin, signOffY + 2, pageWidth - 2 * margin, signOffHeight);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    const colW = (pageWidth - 2 * margin) / 3;
    const sxBase = margin + 4;
    const syRow1 = signOffY + 11;
    const syRow2 = signOffY + 19;
    const labels = ["Prepared by", "Verified by", "Received by (Stores)"];
    labels.forEach((lbl, i) => {
      const x = sxBase + i * colW;
      doc.text(lbl, x, syRow1);
      doc.line(x, syRow1 + 4, x + colW - 8, syRow1 + 4);
      doc.text("Date:", x, syRow2 + 4);
      doc.line(x + 10, syRow2 + 4, x + colW - 8, syRow2 + 4);
    });
    doc.text("Remarks:", margin + 4, signOffY + 30);
    doc.line(margin + 22, signOffY + 30, pageWidth - margin - 4, signOffY + 30);
    doc.line(margin + 4, signOffY + 35, pageWidth - margin - 4, signOffY + 35);

    // --- Final pass: stamp accurate "Page X of N" on every page ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
      doc.setPage(p);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(
        `Page ${p} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: "center" }
      );
    }

    const fname = `rm_required_stores_${(result.branch || "branch").replace(
      /\s+/g,
      "_"
    )}_${todayISO()}.pdf`;
    doc.save(fname);
    toast.success("Stores PDF downloaded");
  };

  return (
    <div className="p-6 md:p-8" data-testid="rm-required-page">
      {/* Header */}
      <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase flex items-center gap-3">
            <Calculator className="w-8 h-8 text-violet-600" />
            RM Required
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Calculate raw material requirement against current branch stock —
            from the production schedule or a manual SKU list.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportPdf}
            disabled={!result || (result.items || []).length === 0}
            className="uppercase text-xs tracking-wide"
            data-testid="rm-required-export-pdf-btn"
            title="Printable stores pick-list (matches on-screen filters)"
          >
            <FileText className="w-4 h-4 mr-2" />
            Export PDF (Stores)
          </Button>
          <Button
            variant="default"
            onClick={exportXlsx}
            disabled={!result || (result.items || []).length === 0}
            className="uppercase text-xs tracking-wide"
            data-testid="rm-required-export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Inputs */}
      <Card className="mb-6">
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-56">
              <Label className="text-xs text-muted-foreground">Branch</Label>
              <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger data-testid="rm-required-branch-select">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((b) => (
                    <SelectItem key={b.name} value={b.name}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 items-center">
              <Label className="text-xs text-muted-foreground mr-1">Mode</Label>
              <Button
                size="sm"
                variant={mode === "schedule" ? "default" : "outline"}
                onClick={() => setMode("schedule")}
                data-testid="rm-required-mode-schedule"
              >
                <Calendar className="w-3.5 h-3.5 mr-1.5" />
                From Schedule
              </Button>
              <Button
                size="sm"
                variant={mode === "manual" ? "default" : "outline"}
                onClick={() => setMode("manual")}
                data-testid="rm-required-mode-manual"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Manual SKUs
              </Button>
            </div>

            {mode === "schedule" && (
              <>
                <div className="w-40">
                  <Label className="text-xs text-muted-foreground">
                    From Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="font-mono"
                    data-testid="rm-required-start-date"
                  />
                </div>
                <div className="w-40">
                  <Label className="text-xs text-muted-foreground">To Date</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="font-mono"
                    data-testid="rm-required-end-date"
                  />
                </div>
              </>
            )}

            <Button
              onClick={calculate}
              disabled={loading}
              className="uppercase text-xs tracking-wide"
              data-testid="rm-required-calculate-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Calculating…
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate
                </>
              )}
            </Button>
          </div>

          {/* Manual rows table */}
          {mode === "manual" && (
            <div className="border rounded-md overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b">
                  <tr>
                    <th className="px-2 py-2 text-center text-[10px] font-mono uppercase w-10">
                      #
                    </th>
                    <th className="px-2 py-2 text-left text-[10px] font-mono uppercase">
                      Buyer SKU
                    </th>
                    <th className="px-2 py-2 text-left text-[10px] font-mono uppercase">
                      Name
                    </th>
                    <th className="px-2 py-2 text-right text-[10px] font-mono uppercase w-32">
                      Quantity
                    </th>
                    <th className="w-12" />
                  </tr>
                </thead>
                <tbody>
                  {manualLines.map((l, i) => (
                    <ManualLineRow
                      key={i}
                      idx={i}
                      line={l}
                      onChange={updateManualLine}
                      onRemove={removeManualLine}
                    />
                  ))}
                </tbody>
              </table>
              <div className="p-2 border-t bg-zinc-50/60 flex justify-between items-center">
                <span className="text-xs text-muted-foreground font-mono">
                  {manualLines.filter((l) => l.buyer_sku_id && Number(l.quantity) > 0).length}{" "}
                  of {manualLines.length} row(s) valid
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addManualLine}
                  data-testid="rm-required-add-row-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Add Row
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Source meta */}
      {result && (
        <div className="mb-4 text-xs text-muted-foreground font-mono">
          Source:{" "}
          {result.mode === "schedule" ? (
            <>
              Production schedules for <strong>{result.branch}</strong> over{" "}
              <strong>{result.source?.period}</strong> —{" "}
              <strong>{result.source?.schedules_considered ?? 0}</strong>{" "}
              schedule(s) considered (excluding CANCELLED / COMPLETED).
            </>
          ) : (
            <>
              Manual input — <strong>{result.source?.lines_processed ?? 0}</strong> of{" "}
              <strong>{result.source?.lines_received ?? 0}</strong> line(s) used.
            </>
          )}
        </div>
      )}

      {/* Warnings */}
      {result && (result.warnings || []).length > 0 && (
        <Card className="mb-6 border-amber-300 bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <strong className="text-xs uppercase tracking-wide text-amber-900">
                {result.warnings.length} Warning
                {result.warnings.length === 1 ? "" : "s"}
              </strong>
            </div>
            <ul className="text-xs font-mono text-amber-900 list-disc pl-6 space-y-0.5">
              {result.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Summary cards + results */}
      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-xs font-mono uppercase text-muted-foreground mb-1">
                  Total RMs Required
                </div>
                <div className="text-3xl font-black">
                  {result.totals?.rms_required ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card
              className={
                (result.totals?.rms_in_shortage ?? 0) > 0
                  ? "border-rose-200 bg-rose-50"
                  : "border-emerald-200 bg-emerald-50"
              }
            >
              <CardContent className="pt-6">
                <div className="text-xs font-mono uppercase text-muted-foreground mb-1">
                  RMs in Shortage
                </div>
                <div
                  className={`text-3xl font-black ${
                    (result.totals?.rms_in_shortage ?? 0) > 0
                      ? "text-rose-600"
                      : "text-emerald-600"
                  }`}
                  data-testid="rm-required-shortage-count"
                >
                  {result.totals?.rms_in_shortage ?? 0}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-xs font-mono uppercase text-muted-foreground mb-1">
                  Branch
                </div>
                <div className="text-xl font-black truncate">
                  {result.branch}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Result filters */}
          <div className="flex flex-wrap gap-3 mb-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search RM ID, description, category…"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-9 font-mono"
                data-testid="rm-required-search-input"
              />
            </div>
            <Button
              size="sm"
              variant={showShortageOnly ? "default" : "outline"}
              onClick={() => setShowShortageOnly((v) => !v)}
              data-testid="rm-required-shortage-toggle"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              {showShortageOnly ? "Showing Shortages Only" : "Show Shortages Only"}
            </Button>
            <span className="text-xs text-muted-foreground font-mono ml-auto">
              {filtered.length} of {result.items?.length || 0} row(s)
            </span>
          </div>

          {/* Results table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-100 border-b">
                    <tr>
                      <th className="h-12 px-2 w-10" />
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        RM ID
                      </th>
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        Description
                      </th>
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        Category
                      </th>
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        Pantone
                      </th>
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        Preferred MB
                      </th>
                      <th className="h-12 px-4 text-left font-mono text-xs uppercase">
                        Unit
                      </th>
                      <th className="h-12 px-4 text-right font-mono text-xs uppercase">
                        Required
                      </th>
                      <th className="h-12 px-4 text-right font-mono text-xs uppercase">
                        In Stock
                      </th>
                      <th className="h-12 px-4 text-right font-mono text-xs uppercase">
                        Shortage
                      </th>
                    </tr>
                  </thead>
                  <tbody data-testid="rm-required-results-tbody">
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="p-10 text-center text-muted-foreground"
                          data-testid="rm-required-empty-state"
                        >
                          <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          {result.items?.length === 0
                            ? "No RM requirements computed — check warnings above."
                            : "No rows match the current filter."}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((it) => {
                        const isOpen = !!expanded[it.rm_id];
                        return (
                          <Fragment key={it.rm_id}>
                            <tr
                              className={`border-t hover:bg-zinc-50/70 ${
                                it.is_shortage ? "bg-rose-50/60" : ""
                              }`}
                              data-testid={`rm-required-row-${it.rm_id}`}
                            >
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(it.rm_id)}
                                  className="text-zinc-500 hover:text-zinc-900"
                                  title="Show contributing SKUs"
                                  data-testid={`rm-required-expand-${it.rm_id}`}
                                >
                                  {isOpen ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                              </td>
                              <td className="p-4 font-mono font-bold">
                                {it.rm_id}
                              </td>
                              <td
                                className="p-4 max-w-[280px] truncate"
                                title={it.description}
                              >
                                {it.description || "—"}
                              </td>
                              <td className="p-4 font-mono text-xs">
                                {it.category || "—"}
                              </td>
                              <td className="p-4 font-mono text-xs"
                                  data-testid={`rm-required-pantone-${it.rm_id}`}>
                                {it.pantone || "—"}
                              </td>
                              <td className="p-4 font-mono text-xs"
                                  data-testid={`rm-required-mb-${it.rm_id}`}>
                                {it.preferred_mb || "—"}
                              </td>
                              <td className="p-4 font-mono text-xs">
                                {it.display_uom || it.unit || "—"}
                                {it.uom_status === "CONVERSION_MISSING" && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-800"
                                    title="Dual-UOM conversion factor missing — comparison skipped"
                                    data-testid={`rm-required-uom-missing-${it.rm_id}`}
                                  >
                                    UOM?
                                  </span>
                                )}
                              </td>
                              <td className="p-4 font-mono text-right font-semibold">
                                {Number(it.required_qty).toLocaleString()}
                              </td>
                              <td className="p-4 font-mono text-right">
                                {it.available_stock == null ? (
                                  <span className="text-amber-700 text-xs">
                                    {Number(it.available_stock_stock_uom ?? 0).toLocaleString()}{" "}
                                    <span className="text-[10px] uppercase">{it.stock_uom || "?"}</span>
                                  </span>
                                ) : (
                                  <>
                                    <div>{Number(it.available_stock).toLocaleString()}</div>
                                    {it.is_dual_uom && it.stock_uom && (
                                      <div className="text-[10px] text-zinc-500">
                                        ≈ {Number(it.available_stock_stock_uom ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} {it.stock_uom}
                                      </div>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="p-4 font-mono text-right">
                                {it.shortage == null ? (
                                  <span className="text-amber-700 text-[10px] uppercase">skipped</span>
                                ) : it.is_shortage ? (
                                  <Badge
                                    variant="destructive"
                                    className="font-mono"
                                  >
                                    {Number(it.shortage).toLocaleString()}
                                  </Badge>
                                ) : (
                                  <span className="text-emerald-700 inline-flex items-center gap-1 text-xs font-bold">
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    OK
                                  </span>
                                )}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr
                                className="bg-zinc-50/80 border-t border-zinc-200"
                                data-testid={`rm-required-contrib-${it.rm_id}`}
                              >
                                <td colSpan={10} className="p-4">
                                  <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground mb-2">
                                    Contributing SKUs ({(it.contributing_skus || []).length})
                                  </div>
                                  {(it.contributing_skus || []).length === 0 ? (
                                    <div className="text-xs text-muted-foreground">
                                      No contributing SKUs.
                                    </div>
                                  ) : (
                                    <table className="w-full text-xs">
                                      <thead className="text-muted-foreground">
                                        <tr>
                                          <th className="text-left py-1">
                                            Buyer SKU
                                          </th>
                                          <th className="text-left py-1">Name</th>
                                          <th className="text-right py-1">
                                            Qty
                                          </th>
                                          <th className="text-right py-1">
                                            RM / Unit
                                          </th>
                                          <th className="text-right py-1">
                                            Subtotal
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(it.contributing_skus || []).map(
                                          (s, idx) => (
                                            <tr
                                              key={`${s.buyer_sku_id}-${idx}`}
                                              className="border-t border-zinc-200"
                                            >
                                              <td className="py-1 font-mono">
                                                {s.buyer_sku_id}
                                              </td>
                                              <td className="py-1 truncate max-w-[260px]">
                                                {s.buyer_sku_name || "—"}
                                              </td>
                                              <td className="py-1 font-mono text-right">
                                                {s.qty}
                                              </td>
                                              <td className="py-1 font-mono text-right">
                                                {s.rm_per_unit}
                                              </td>
                                              <td className="py-1 font-mono text-right font-semibold">
                                                {(
                                                  Number(s.qty) *
                                                  Number(s.rm_per_unit)
                                                ).toFixed(4)}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!result && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Calculator className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground">
              Pick a branch, choose a mode, then hit{" "}
              <strong>Calculate</strong> to see the RM requirement against current stock.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RMRequired;
